import { resolveEndpoint } from "@/config/endpoint-resolver";
import { ApolloClient, DefaultOptions } from "apollo-client";
import { createHttpLink } from "apollo-link-http";
import { InMemoryCache } from "apollo-cache-inmemory";
import { setContext } from "apollo-link-context";
import { SharedCallbacks } from "./shared-resources";
import { ApolloLink } from "apollo-link";
import { LogService } from "@/services/logging";
import { showSessionExpiredPopup } from "./helpers/auth";
import { onError } from "apollo-link-error";

// HTTP connection to the API
const httpLink = createHttpLink({
  // uri as a function is re-evaluated per operation, so a Flagsmith per-group
  // override is applied at request-time, not pinned at link creation.
  uri: () => resolveEndpoint("VITE_GQL_API"),
});

/**
 * We need a token to be supplied to be able to query data
 * If the user is logged in and has smtzAuth then we use it
 * If the user is logged in but does not have smtzAuth then we request it
 * if the requesting smtzAuth fails then we show an error and logout if user is unauthenticated
 */

const authLink = setContext(async (_, { headers }) => {
  const gqlAuthHeader = SharedCallbacks.gqlAuthHeader;

  console.log("Appolo context");
  const newHeaders = {
    ...headers,
  };

  if (gqlAuthHeader) {
    try {
      newHeaders["smtz-auth"] = await gqlAuthHeader();
    } catch (error) {
      LogService.info(
        "Logging user out after attempting an unauthorized call to graphql",
        { error }
      );
      showSessionExpiredPopup();
      // we still throw the error to prevent callers from continuing executions
      throw error;
    }
  }
  return { headers: newHeaders };
});

// Cache implementation
const cache = new InMemoryCache();

// 1- For now, we do not need apollo client to store data in cache
// we need every graphql query to fetch from remote, not to read from apollo cache
// 2- In future (TODO), we need to set cases where we can cache and read from cache
// and cases where we should fetch from remote again
// e.g: Set cache as default (just remove the use of the below defaultOptions) and when we have
// and when we have a posConfig updated (PSC updated or timeSlot updated ...etc) we override the default options
// inside the function running the query (e.g: getTicketGroupsProductCategories()) so that the query triggers a fetch
// instead of just reading from apollo cache
const defaultOption: DefaultOptions = {
  watchQuery: {
    fetchPolicy: "no-cache",
    errorPolicy: "ignore",
  },
  query: {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  },
};
// Create a new link that logs the response time for each request
const timingLink = new ApolloLink((operation, forward) => {
  const startTime = Date.now();
  return forward(operation).map((response) => {
    const endTime = Date.now();
    const duration = endTime - startTime;
    LogService.info(
      `Request for ${operation.operationName} took ${duration}ms`
    );
    return response;
  });
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      LogService.info(
        `Request for ${operation.operationName} failed due to a GraphQL error`,
        {
          message,
          locations,
          path,
        }
      );
    });
  }
  if (networkError) {
    LogService.info(
      `Request for ${operation.operationName} failed due to a network error`,
      { networkError }
    );
    // Mirror the axios REST interceptor: a 401/403 from the GraphQL server
    // means the access token is no longer valid, so we trigger the
    // session-expired flow instead of a generic error.
    const statusCode = (networkError as { statusCode?: number }).statusCode;
    if (statusCode === 401 || statusCode === 403) {
      LogService.info(
        "Logging user out after an Unauthorized/Forbidden GraphQL response",
        { operation: operation.operationName, statusCode }
      );
      showSessionExpiredPopup();
    }
  }
});

// Create the apollo client
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([timingLink, errorLink, authLink.concat(httpLink)]),
  cache,
  defaultOptions: defaultOption,
});
